import { type SDKOptions, createElement, init } from '@airwallex/components-sdk'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState } from 'react'

import { useConfig } from '@/providers/config'
import type { ConnectedAccount } from '@/providers/connectedAccount/types'
import { useUserContext } from '@/providers/user/useUserContext'
import Loading from '@/ui-components/Loading'

import { KYCType, type KYCTypeUnion } from './types'
import { generateCodeChallengeFromVerifier, generateCodeVerifier } from './util/KYCCodeChallenge'

interface KYCRFIComponentProps {
  connectedAccount: ConnectedAccount
  type: KYCTypeUnion
}

export const KYCRFIComponent = ({ connectedAccount, type }: KYCRFIComponentProps) => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId } = useUserContext()
  const { config } = useConfig()

  const [isLoadingAuthCode, setIsLoadingAuthCode] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!activeOrganizationId || !connectedAccount.id || !config?.awx.clientId) {
      return
    }
    const mountKYC = async () => {
      setError(null)
      try {
        const codeVerifier = generateCodeVerifier()
        const authCode = await getAuthCode(codeVerifier)
        if (!authCode) {
          throw new Error('Unable to get auth code')
        }
        const options = {
          locale: 'en',
          env: 'demo',
          enabledElements: [type],
          authCode: authCode,
          clientId: config.awx.clientId,
          codeVerifier: codeVerifier,
        } as SDKOptions
        await init(options)
        const element = await createElement(type, {
          hideHeader: true,
          hideNav: true,
        })
        element.mount(`${type}-container`)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize KYC component'
        setError(errorMessage)
        console.error('KYC Component Error:', err)
      }
    }
    mountKYC()
    // We intentionally use an empty dependency array to ensure this effect only runs once on mount.
    // This gives us explicit control over when the KYC/RFI mounting happens, rather than
    // automatically re-running when dependencies change. The component is designed to be
    // mounted only when the user explicitly proceeds to the KYC/RFI flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getAuthCode = async (codeVerifier: string): Promise<string | null> => {
    setIsLoadingAuthCode(true)
    try {
      const endpoint =
        type === KYCType.KYC
          ? `/api/pay/org/${activeOrganizationId}/connected-account/${connectedAccount.id}/onboarding/authorize`
          : `/api/pay/org/${activeOrganizationId}/connected-account/${connectedAccount.id}/rfi/authorize`

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAccessToken()}`,
        },
        body: JSON.stringify({
          codeChallenge: await generateCodeChallengeFromVerifier(codeVerifier),
        }),
      })

      if (!res.ok) {
        throw new Error('Failed to get auth code')
      }

      const data = (await res.json()) as { authCode: string }
      return data.authCode
    } finally {
      setIsLoadingAuthCode(false)
    }
  }

  return (
    <div className="h-[calc(100vh-7.25rem)] relative">
      <div
        id={`${type}-container`}
        className="h-full"
      ></div>
      {isLoadingAuthCode && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
          <Loading />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">Failed to load KYC component</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
          </div>
        </div>
      )}
    </div>
  )
}
